import type { JSX } from 'react';
import { useEffect, useRef, useState } from 'react';
import { Autocomplete } from '@mantine/core';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ApiError, api, projectApi } from '../api';
import { useCan } from './useMe';

/**
 * The project's location (ADR 0007): one field on the project itself, shown
 * wherever it matters - master data, cost planning - so a change in one place
 * is the change in the other. Saved on leaving the field or Enter; editing
 * needs projects.write, like number and name. `suggestions` offers known
 * places (e.g. the districts of the regional factors) without restricting.
 */
export function LocationField({ projectId, suggestions = [], label = 'Standort', description }: {
  projectId: string;
  suggestions?: readonly string[];
  label?: string;
  description?: string;
}): JSX.Element {
  const queryClient = useQueryClient();
  const canEdit = useCan('projects.write');
  const projects = useQuery({ queryKey: ['projects'], queryFn: api.projects });
  const project = projects.data?.find((p) => p.id === projectId);
  const [value, setValueState] = useState(project?.location ?? '');
  // The latest text, also for the blur that follows choosing a suggestion:
  // with the value from the closure it saved the half-typed text again and
  // the chosen place jumped back.
  const latest = useRef(value);
  const sent = useRef<string | null>(null);
  const setValue = (v: string): void => { latest.current = v; setValueState(v); };
  useEffect(() => { setValue(project?.location ?? ''); sent.current = null; }, [project?.location]);
  const save = useMutation({
    mutationFn: (location: string) => projectApi.update(projectId, { location: location.trim() || null, version: project?.version }),
    onSettled: () => queryClient.invalidateQueries({ queryKey: ['projects'] }),
  });
  const commit = (next: string): void => {
    const v = next.trim();
    if ((v || null) === (project?.location ?? null) || v === sent.current) return;
    sent.current = v;
    save.mutate(v, { onError: () => { sent.current = null; } });
  };
  return (
    <Autocomplete
      label={label}
      description={description}
      placeholder="z. B. Berlin, Stadt"
      data={[...suggestions]}
      limit={12}
      value={value}
      onChange={setValue}
      onOptionSubmit={(v) => { setValue(v); commit(v); }}
      // After the event: Enter on a highlighted suggestion first fires this
      // keydown and only then the option - saving at once stored the
      // half-typed text, and the chosen place failed on the version check.
      onBlur={() => setTimeout(() => commit(latest.current), 0)}
      onKeyDown={(e) => { if (e.key === 'Enter') setTimeout(() => commit(latest.current), 0); }}
      disabled={!canEdit || !project}
      error={save.error instanceof ApiError ? save.error.message : save.isError ? 'Speichern fehlgeschlagen.' : undefined}
    />
  );
}
